// src/pages/UserPage/sections/OrganizationDeliveries/DeliveryEditModal.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Box, Stack,
  MenuItem, CircularProgress, IconButton, Divider, Chip,
} from '@mui/material';
import { X } from 'lucide-react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../../firebase/config';
import { organizationUpdateDeliveryFn } from "../../../../firebase/deliveryFunctions";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(ms) {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('fi-FI', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function fmtDatetime(ms) {
  if (!ms) return '—';
  return new Date(ms).toLocaleString('fi-FI', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function msToLocal(ms) {
  if (!ms) return '';
  const d   = new Date(ms);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localToMs(str) {
  return str ? new Date(str).getTime() : null;
}

const CHECK_LABELS = {
  origin:      'Nouto-osoite',
  destination: 'Toimitusosoite',
  times:       'Ajankohta',
  reward:      'Palkkio',
};

const CORRECTION_LABELS = {
  origin:      'Oikea nouto-osoite',
  destination: 'Oikea toimitusosoite',
  pickupTime:  'Oikea noutoaika',
  targetTime:  'Oikea kohdeaika',
  reward:      'Oikea palkkio',
};

const INVOICE_STATUSES = [
  { value: 'pending',  label: 'Odottaa' },
  { value: 'invoiced', label: 'Laskutettu' },
  { value: 'paid',     label: 'Maksettu' },
];

const PURCHASE_STATUSES = [
  { value: 'pending', label: 'Odottaa' },
  { value: 'paid',    label: 'Maksettu' },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function SectionLabel({ children }) {
  return (
    <Typography
      variant="caption"
      fontWeight={700}
      color="text.secondary"
      sx={{ textTransform: 'uppercase', letterSpacing: '0.07em', fontSize: '0.68rem' }}
    >
      {children}
    </Typography>
  );
}

function Row({ children }) {
  return <Stack direction="row" spacing={1.5}>{children}</Stack>;
}

function PartyFields({ label, prefix, values, onChange }) {
  return (
    <Box>
      <SectionLabel>{label}</SectionLabel>
      <Stack spacing={1.5} sx={{ mt: 1 }}>
        <Row>
          <TextField
            label="Yrityksen nimi" fullWidth size="small"
            value={values[`${prefix}.companyName`] ?? ''}
            onChange={onChange(`${prefix}.companyName`)}
          />
          <TextField
            label="Y-tunnus" fullWidth size="small"
            value={values[`${prefix}.yNumber`] ?? ''}
            onChange={onChange(`${prefix}.yNumber`)}
          />
        </Row>
        <TextField
          label="IBAN" fullWidth size="small"
          value={values[`${prefix}.iban`] ?? ''}
          onChange={onChange(`${prefix}.iban`)}
        />
      </Stack>
    </Box>
  );
}

function ReviewDataSection({ reviewData }) {
  if (!reviewData) return null;

  const failedChecks = Object.entries(reviewData.checks || {})
    .filter(([, v]) => v === false)
    .map(([k]) => k);

  const corrections = Object.entries(reviewData.corrections || {})
    .filter(([, v]) => v != null);

  return (
    <Box sx={{ p: 2, bgcolor: 'error.50', border: '1px solid', borderColor: 'error.200', borderRadius: 1.5 }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <SectionLabel>Reklamaatio</SectionLabel>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {[1, 2, 3, 4, 5].map((s) => (
            <Typography key={s} variant="caption"
              sx={{ color: s <= reviewData.stars ? 'warning.main' : 'text.disabled' }}>
              ★
            </Typography>
          ))}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
            {reviewData.stars}/5
          </Typography>
        </Box>
      </Box>

      {/* Failed checks */}
      {failedChecks.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Ongelmat:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {failedChecks.map((k) => (
              <Chip key={k} label={CHECK_LABELS[k] || k} color="error" size="small"
                sx={{ height: 20, fontSize: '0.65rem' }} />
            ))}
          </Box>
        </Box>
      )}

      {/* Corrections */}
      {corrections.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
            Korjaukset:
          </Typography>
          <Stack spacing={0.75}>
            {corrections.map(([k, v]) => (
              <Box key={k} sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
                <Typography variant="caption" color="text.secondary"
                  sx={{ minWidth: 130, flexShrink: 0 }}>
                  {CORRECTION_LABELS[k] || k}:
                </Typography>
                <Typography variant="caption" fontWeight={600}>
                  {k === 'pickupTime' || k === 'targetTime'
                    ? fmtDatetime(v)
                    : k === 'reward'
                      ? `${v} €`
                      : v}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {/* General note */}
      {reviewData.generalNote && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
            Yleinen huomio:
          </Typography>
          <Typography variant="caption">{reviewData.generalNote}</Typography>
        </Box>
      )}

      {/* Footer */}
      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
        Kuriiri: {reviewData.reviewerId} · {fmtDate(reviewData.createdAtMs)}
      </Typography>
    </Box>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export function DeliveryEditModal({ open, delivery, organizationId, onClose, onSaved }) {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!delivery) return;
    setValues({
      origin:         delivery.origin        ?? '',
      destination:    delivery.destination   ?? '',
      completedAtMs:  msToLocal(delivery.completedAtMs),
      price:          delivery.price         ?? '',
      reward:         delivery.reward        ?? '',
      invoiceStatus:  delivery.invoiceStatus  || 'pending',
      purchaseStatus: delivery.purchaseStatus || 'pending',
      // Billing party (who sends the invoice)
      'billing.companyName': delivery.billing?.companyName ?? '',
      'billing.yNumber':     delivery.billing?.yNumber     ?? '',
      'billing.iban':        delivery.billing?.iban        ?? '',
      // Payment party (who receives money)
      'payment.companyName': delivery.payment?.companyName ?? '',
      'payment.yNumber':     delivery.payment?.yNumber     ?? '',
      'payment.iban':        delivery.payment?.iban        ?? '',
    });
  }, [delivery]);

  if (!delivery) return null;

  const set = (field) => (e) => setValues((v) => ({ ...v, [field]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = {
        origin:         values.origin      || null,
        destination:    values.destination || null,
        completedAtMs:  localToMs(values.completedAtMs),
        price:          values.price  !== '' ? Number(values.price)  : null,
        reward:         values.reward !== '' ? Number(values.reward) : null,
        invoiceStatus:  values.invoiceStatus,
        purchaseStatus: values.purchaseStatus,
        billing: {
          companyName: values['billing.companyName'] || null,
          yNumber:     values['billing.yNumber']     || null,
          iban:        values['billing.iban']        || null,
        },
        payment: {
          companyName: values['payment.companyName'] || null,
          yNumber:     values['payment.yNumber']     || null,
          iban:        values['payment.iban']        || null,
        },
      };
console.log('delivery.deliveryId', delivery.deliveryId)

      await organizationUpdateDeliveryFn({
        organizationId,
        deliveryId:  delivery.deliveryId,
        courierId:   delivery.courierId ?? null,
        updates: updated,
      });
      onSaved?.({ ...delivery, ...updated });
      onClose();
    } catch (err) {
      console.error('DeliveryEditModal save failed:', err);
      alert(err.message || 'Tallennus epäonnistui');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>Muokkaa toimitusta</Typography>
          <Typography variant="caption" color="text.secondary" fontFamily="monospace">
            {delivery.deliveryId || delivery.id}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={saving}><X size={18} /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={2.5}>

          {/* ── Review / dispute ── */}
          {delivery.reviewData && (
            <>
              <ReviewDataSection reviewData={delivery.reviewData} />
              <Divider />
            </>
          )}

          {/* ── Route ── */}
          <Box>
            <SectionLabel>Reitti</SectionLabel>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <TextField
                label="Nouto-osoite" fullWidth size="small"
                value={values.origin ?? ''} onChange={set('origin')}
              />
              <TextField
                label="Toimitusosoite" fullWidth size="small"
                value={values.destination ?? ''} onChange={set('destination')}
              />
            </Stack>
          </Box>

          <Divider />

          {/* ── Timing ── */}
          <Box>
            <SectionLabel>Ajankohta</SectionLabel>
            <Box sx={{ mt: 1 }}>
              <TextField
                label="Valmistumisaika" type="datetime-local" fullWidth size="small"
                value={values.completedAtMs ?? ''} onChange={set('completedAtMs')}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>

          <Divider />

          {/* ── Pricing ── */}
          <Box>
            <SectionLabel>Hinnoittelu</SectionLabel>
            <Row sx={{ mt: 1 }}>
              <TextField
                label="Hinta (€)" type="number" fullWidth size="small"
                value={values.price ?? ''} onChange={set('price')}
                inputProps={{ min: 0, step: 0.01 }}
                sx={{ mt: 1 }}
              />
              <TextField
                label="Kuljettajan palkkio (€)" type="number" fullWidth size="small"
                value={values.reward ?? ''} onChange={set('reward')}
                inputProps={{ min: 0, step: 0.01 }}
                sx={{ mt: 1 }}
              />
            </Row>
          </Box>

          <Divider />

          {/* ── Billing & Payment parties ── */}
          <PartyFields
            label="Laskutustiedot"
            prefix="billing"
            values={values}
            onChange={set}
          />

          <Divider />

          <PartyFields
            label="Maksutiedot"
            prefix="payment"
            values={values}
            onChange={set}
          />

          <Divider />

          {/* ── Statuses ── */}
          <Box>
            <SectionLabel>Tilat</SectionLabel>
            <Row sx={{ mt: 1 }}>
              <TextField
                label="Laskutustila" select fullWidth size="small"
                value={values.invoiceStatus ?? 'pending'} onChange={set('invoiceStatus')}
                sx={{ mt: 1 }}
              >
                {INVOICE_STATUSES.map((s) => (
                  <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="Maksutila" select fullWidth size="small"
                value={values.purchaseStatus ?? 'pending'} onChange={set('purchaseStatus')}
                sx={{ mt: 1 }}
              >
                {PURCHASE_STATUSES.map((s) => (
                  <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                ))}
              </TextField>
            </Row>
          </Box>

        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>Peruuta</Button>
        <Button
          variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {saving ? 'Tallennetaan...' : 'Tallenna'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}